import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownViewer from './MarkdownViewer';

const DocsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, 1fr) 3fr;
  gap: 2rem;
  margin: 2rem auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 100px;
  
  @media (max-width: 768px) {
    position: relative;
    top: 0;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  color: #333;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.accentColor || '#0066cc'};
`;

const NavGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const NavGroupTitle = styled.h4`
  font-size: 1rem;
  margin-bottom: 0.8rem;
  color: #555;
  font-weight: 600;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 0.5rem;
`;

const NavLink = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 5px;
  background: ${props => props.isActive ? `rgba(${props.rgbColor || '0, 102, 204'}, 0.1)` : 'transparent'};
  color: ${props => props.isActive ? props.accentColor || '#0066cc' : '#666'};
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s;
  border-left: 3px solid ${props => props.isActive ? props.accentColor || '#0066cc' : 'transparent'};
  
  &:hover {
    background: ${props => `rgba(${props.rgbColor || '0, 102, 204'}, 0.05)`};
    color: ${props => props.accentColor || '#0066cc'};
  }
`;

const Content = styled.div`
  min-height: 500px;
`;

const MobileNavToggle = styled.button`
  display: none;
  width: 100%;
  padding: 0.8rem;
  background: ${props => props.accentColor || '#0066cc'};
  color: white;
  border: none;
  border-radius: 5px;
  margin-bottom: 1rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const DocsNavigator = ({ 
  title = 'Documentation',
  sections = [],
  accentColor = '#0066cc',
  initialPage = null
}) => {
  const [activePage, setActivePage] = useState(null);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isBrowser = typeof window !== 'undefined';
  
  // Convert hex to rgb for transparency use
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` 
      : null;
  };
  
  const rgbColor = hexToRgb(accentColor);
  
  useEffect(() => {
    // Set initial page
    if (initialPage) {
      setActivePage(initialPage);
    } else if (sections.length > 0 && sections[0]?.pages?.length > 0) {
      setActivePage(sections[0].pages[0]);
    }
    
    // Check if mobile on initial render
    if (isBrowser) {
      setIsMobile(window.innerWidth <= 768);
      
      // Add resize listener
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      window.addEventListener('resize', handleResize);
      
      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [initialPage, sections, isBrowser]);
  
  const handlePageChange = (page) => {
    setActivePage(page);
    setShowMobileNav(false);
    // Scroll to top of content on page change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <>
      <MobileNavToggle 
        onClick={() => setShowMobileNav(!showMobileNav)}
        accentColor={accentColor}
      >
        {title}
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {showMobileNav ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </>
          ) : (
            <>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </>
          )}
        </svg>
      </MobileNavToggle>
      
      <DocsContainer>
        <AnimatePresence>
          {(showMobileNav || !isMobile) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Sidebar>
                <SidebarTitle accentColor={accentColor}>{title}</SidebarTitle>
                {sections.map((section, index) => (
                  <NavGroup key={index}>
                    <NavGroupTitle>{section.title || 'Untitled Section'}</NavGroupTitle>
                    <NavList>
                      {(section.pages || []).map((page, pageIndex) => (
                        <NavItem key={pageIndex}>
                          <NavLink 
                            onClick={() => handlePageChange(page)}
                            isActive={activePage?.id === page.id}
                            accentColor={accentColor}
                            rgbColor={rgbColor}
                          >
                            {page.title}
                          </NavLink>
                        </NavItem>
                      ))}
                    </NavList>
                  </NavGroup>
                ))}
              </Sidebar>
            </motion.div>
          )}
        </AnimatePresence>
        
        <Content>
          {activePage && (
            <motion.div
              key={activePage.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <MarkdownViewer content={activePage.content} />
            </motion.div>
          )}
        </Content>
      </DocsContainer>
    </>
  );
};

export default DocsNavigator;
